import React, { useMemo } from 'react';
import { Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { MobileComponentWrapper } from './PasswordHelper.styles';
import { PasswordHelperProps } from './PasswordHelper.types';
import { UncheckedCircleIcon, CheckCircleFilledIcon } from 'src/packages/icons';
import { Flex, Text } from 'src/packages/core';

const PasswordHelper: React.FC<PasswordHelperProps> = ({
  passwordConditions,
  anchorEl,
  id,
  password,
  placement = 'right-start',
}: PasswordHelperProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const Conditions = useMemo(() => {
    return passwordConditions.map((item, index) => {
      return (
        <Flex key={index} alignItems={'center'} gap={3} mb={2}>
          {item.regex.test(password) ? (
            <CheckCircleFilledIcon size={21} color={theme.colors.palette.primary} />
          ) : (
            <UncheckedCircleIcon size={21} />
          )}

          <Text
            text={t(item.message)}
            variant='p2'
            color={
              item.regex.test(password) ? theme.colors.text.tertiary : theme.colors.palette.red
            }
          />
        </Flex>
      );
    });
  }, [password]);

  const PasswordHelperMobile = useMemo(() => {
    return anchorEl ? (
      <MobileComponentWrapper>
        <Text text={t('password.custom_validation.title')} variant='p2' mb={3} />

        {passwordConditions.map((item, index) => {
          return (
            <Flex key={index} alignItems={'center'} gap={3} mb={2}>
              {item.regex.test(password) ? (
                <CheckCircleFilledIcon size={21} color={theme.colors.palette.primary} />
              ) : (
                <UncheckedCircleIcon size={21} />
              )}

              <Text
                text={t(item.message)}
                variant='p2'
                color={
                  item.regex.test(password) ? theme.colors.text.tertiary : theme.colors.palette.red
                }
              />
            </Flex>
          );
        })}
      </MobileComponentWrapper>
    ) : null;
  }, [password, anchorEl, passwordConditions]);

  return (
    <>
      <Popper
        open={true}
        anchorEl={anchorEl}
        id={anchorEl === null ? undefined : id}
        placement={placement}
        disablePortal={false}
        className={`custom-popper ${placement}`}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 20],
            },
          },
          {
            name: 'arrow',
            enabled: false,
            options: {
              element: anchorEl,
            },
          },
        ]}
      >
        <Flex flexDirection={'column'} p={5}>
          <Text text={t('password.custom_validation.title')} variant='p2' mb={3} />

          {Conditions}
        </Flex>
      </Popper>

      {PasswordHelperMobile}
    </>
  );
};

export default PasswordHelper;
